import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Stack,
  Chip,
  LinearProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  IconButton,
  Tooltip,
  Paper
} from '@mui/material';
import {
  Add as AddIcon,
  CalendarToday as CalendarIcon,
  Event as EventIcon,
  Schedule as ScheduleIcon,
  Person as PersonIcon,
  Pets as PetIcon,
  Vaccines as VaccineIcon,
  LocalHospital as HospitalIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ViewWeek as WeekViewIcon,
  ViewModule as MonthViewIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@contexts/AuthContext';
import { 
  shelterService, 
  getCalendarEvents, 
  getCalendarEvent,
  createCalendarEvent,
  updateCalendarEvent,
  deleteCalendarEvent,
  CalendarEvent
} from '@services/sheltersService';


const ShelterCalendarPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState<'month' | 'week'>('month');
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [eventDialogOpen, setEventDialogOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);
  const [newEvent, setNewEvent] = useState({
    title: '',
    type: 'visit' as CalendarEvent['type'],
    start: '',
    end: '',
    description: '',
    applicantId: '',
    animalId: ''
  });

  useEffect(() => {
    loadEvents();
  }, [selectedDate]);

  const loadEvents = async () => {
    try {
      setLoading(true);
      
      if (!user?.shelterId) {
        throw new Error('Shelter ID not found');
      }

      // Calculează intervalul pentru luna curentă
      const startOfMonth = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1);
      const endOfMonth = new Date(selectedDate.getFullYear(), selectedDate.getMonth() + 1, 0);
      
      const response = await getCalendarEvents({
        startDate: startOfMonth.toISOString(),
        endDate: endOfMonth.toISOString()
      });
      
      setEvents(response.data || []);
    } catch (error) {
      console.error('Error loading events:', error);
      setEvents([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateEvent = () => {
    setSelectedEvent(null);
    setNewEvent({
      title: '',
      type: 'visit',
      start: '',
      end: '',
      description: '',
      applicantId: '',
      animalId: ''
    });
    setEventDialogOpen(true);
  };

  const handleEditEvent = (event: CalendarEvent) => {
    setSelectedEvent(event);
    setNewEvent({
      title: event.title,
      type: event.type,
      start: event.start,
      end: event.end,
      description: event.description || '',
      applicantId: event.applicant?.id || '',
      animalId: event.animal?.id || ''
    });
    setEventDialogOpen(true);
  };

  const handleSaveEvent = async () => {
    try {
      if (!user?.shelterId) return;

      if (selectedEvent) {
        // Actualizează eveniment existent
        await updateCalendarEvent(selectedEvent.id, {
          title: newEvent.title,
          type: newEvent.type,
          start: newEvent.start,
          end: newEvent.end,
          description: newEvent.description
        });
      } else {
        // Creează eveniment nou
        await createCalendarEvent({
          title: newEvent.title,
          type: newEvent.type,
          start: newEvent.start,
          end: newEvent.end,
          description: newEvent.description,
          applicationId: newEvent.applicantId || undefined,
          animalId: newEvent.animalId || undefined
        });
      }
      
      setEventDialogOpen(false);
      loadEvents();
    } catch (error) {
      console.error('Error saving event:', error);
    }
  };

  const handleDeleteEvent = async (eventId: string) => {
    try {
      await deleteCalendarEvent(eventId);
      loadEvents();
    } catch (error) {
      console.error('Error deleting event:', error);
    }
  };

  const getEventTypeIcon = (type: string) => {
    switch (type) {
      case 'visit': return <PersonIcon />;
      case 'interview': return <EventIcon />;
      case 'handover': return <PetIcon />;
      case 'vaccination': return <VaccineIcon />;
      case 'sterilization': return <HospitalIcon />;
      default: return <EventIcon />;
    }
  };

  const getEventTypeColor = (type: string) => {
    switch (type) {
      case 'visit': return 'primary';
      case 'interview': return 'info';
      case 'handover': return 'success';
      case 'vaccination': return 'warning';
      case 'sterilization': return 'error';
      default: return 'default';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'scheduled': return 'info';
      case 'completed': return 'success';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <LinearProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Box>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            Calendar Programări
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Gestionează vizitele, interviurile și evenimentele
          </Typography>
        </Box>
        <Stack direction="row" spacing={2}>
          <Button
            variant={viewMode === 'month' ? 'contained' : 'outlined'}
            startIcon={<MonthViewIcon />}
            onClick={() => setViewMode('month')}
          >
            Lună
          </Button>
          <Button
            variant={viewMode === 'week' ? 'contained' : 'outlined'}
            startIcon={<WeekViewIcon />}
            onClick={() => setViewMode('week')}
          >
            Săptămână
          </Button>
          {user?.role === 'shelter_admin' && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreateEvent}
            >
              Programează Eveniment
            </Button>
          )}
        </Stack>
      </Box>

      {/* Calendar View */}
      <Card>
        <CardContent>
          {events.length === 0 ? (
            <Alert severity="info">
              Nu ai evenimente programate. Adaugă primul eveniment pentru a începe!
            </Alert>
          ) : (
            <Stack spacing={2}>
              {events.map((event) => (
                <Paper key={event.id} sx={{ p: 2 }}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Stack direction="row" alignItems="center" spacing={2}>
                      <Chip
                        icon={getEventTypeIcon(event.type)}
                        label={event.type}
                        color={getEventTypeColor(event.type)}
                        size="small"
                      />
                      <Box>
                        <Typography variant="h6" fontWeight="bold">
                          {event.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {new Date(event.start).toLocaleString()} - {new Date(event.end).toLocaleString()}
                        </Typography>
                        {event.description && (
                          <Typography variant="body2" color="text.secondary">
                            {event.description}
                          </Typography>
                        )}
                        {event.applicant && (
                          <Typography variant="body2" color="text.secondary">
                            <strong>Aplicant:</strong> {event.applicant.name} ({event.applicant.email})
                          </Typography>
                        )}
                        {event.animal && (
                          <Typography variant="body2" color="text.secondary">
                            <strong>Animal:</strong> {event.animal.name} ({event.animal.species})
                          </Typography>
                        )}
                      </Box>
                    </Stack>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Chip
                        label={event.status}
                        color={getStatusColor(event.status)}
                        size="small"
                      />
                      {user?.role === 'shelter_admin' && (
                        <>
                          <Tooltip title="Editează">
                            <IconButton
                              size="small"
                              onClick={() => handleEditEvent(event)}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Șterge">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteEvent(event.id)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </Stack>
                  </Stack>
                </Paper>
              ))}
            </Stack>
          )}
        </CardContent>
      </Card>

      {/* Event Dialog */}
      <Dialog open={eventDialogOpen} onClose={() => setEventDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {selectedEvent ? 'Editează Eveniment' : 'Programează Eveniment'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              label="Titlu"
              value={newEvent.title}
              onChange={(e) => setNewEvent({ ...newEvent, title: e.target.value })}
              required
            />
            <TextField
              fullWidth
              select
              label="Tip eveniment"
              value={newEvent.type}
              onChange={(e) => setNewEvent({ ...newEvent, type: e.target.value as CalendarEvent['type'] })}
            >
              <MenuItem value="visit">Vizită adopție</MenuItem>
              <MenuItem value="interview">Interviu</MenuItem>
              <MenuItem value="handover">Predare</MenuItem>
              <MenuItem value="vaccination">Vaccinare</MenuItem>
              <MenuItem value="sterilization">Sterilizare</MenuItem>
              <MenuItem value="other">Altul</MenuItem>
            </TextField>
            <TextField
              fullWidth
              type="datetime-local"
              label="Data și ora început"
              value={newEvent.start}
              onChange={(e) => setNewEvent({ ...newEvent, start: e.target.value })}
              InputLabelProps={{ shrink: true }}
              required
            />
            <TextField
              fullWidth
              type="datetime-local"
              label="Data și ora sfârșit"
              value={newEvent.end}
              onChange={(e) => setNewEvent({ ...newEvent, end: e.target.value })}
              InputLabelProps={{ shrink: true }}
              required
            />
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Descriere"
              value={newEvent.description}
              onChange={(e) => setNewEvent({ ...newEvent, description: e.target.value })}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEventDialogOpen(false)}>Anulează</Button>
          {user?.role === 'shelter_admin' && (
            <Button variant="contained" onClick={handleSaveEvent}>
              {selectedEvent ? 'Actualizează' : 'Creează'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ShelterCalendarPage;
